import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { tableReservationsApi } from "../../../api/tableReservations";

const STATUS_OPTIONS = [
  ["pending", "Pendiente"],
  ["confirmed", "Confirmada"],
  ["completed", "Completada"],
  ["cancelled", "Cancelada"],
  ["no_show", "No asistió"],
];

const STATUS_COLORS = {
  pending: "warning",
  confirmed: "success",
  completed: "default",
  cancelled: "error",
  no_show: "default",
};

const emptyForm = () => ({
  fullName: "",
  phone: "",
  email: "",
  startAt: "",
  partySize: 2,
  durationMinutes: 90,
  status: "confirmed",
  tableIds: [],
  notes: "",
});

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function dateTimeLabel(value, timezone) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: timezone || undefined,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function statusLabel(status) {
  return STATUS_OPTIONS.find(([value]) => value === status)?.[1] || status;
}

function tableLabel(table) {
  if (!table) return "Sin mesa asignada";
  const name = table.name ? ` · ${table.name}` : "";
  const sector = table.sector ? ` · ${table.sector}` : "";
  return `Mesa ${table.identifier}${name}${sector}`;
}

function reservationTables(reservation) {
  if (Array.isArray(reservation?.tableSnapshots) && reservation.tableSnapshots.length) {
    return reservation.tableSnapshots;
  }
  return reservation?.tableSnapshot ? [reservation.tableSnapshot] : [];
}

function assignmentLabel(tables) {
  if (!tables?.length) return "Sin mesa asignada";
  return tables.map((table) => `Mesa ${table.identifier}${table.name ? ` (${table.name})` : ""}`).join(" + ");
}

function sectorsLabel(tables) {
  const sectors = Array.from(new Set((tables || []).map((table) => table.sector).filter(Boolean)));
  return sectors.length ? sectors.join(" / ") : "Sin sector";
}

export default function TableReservationsPanel({ projectId, canManage = true }) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [reservations, setReservations] = React.useState([]);
  const [availability, setAvailability] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState("");
  const [formOpen, setFormOpen] = React.useState(false);
  const [expandedNotes, setExpandedNotes] = React.useState(() => new Set());
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const timezone = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Mendoza",
    [],
  );

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const rows = await tableReservationsApi.list(projectId);
      setReservations(rows);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las reservas gastronómicas.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const setField = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(["startAt", "partySize", "durationMinutes"].includes(field) ? { tableIds: [] } : {}),
    }));
    if (["startAt", "partySize", "durationMinutes"].includes(field)) setAvailability(null);
    setMessage("");
  };

  const setSelectedTables = (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      tableIds: Array.isArray(value) ? value : String(value || "").split(",").filter(Boolean),
    }));
    setError("");
    setMessage("");
  };

  const checkAvailability = async () => {
    if (!canManage) return;
    if (!form.startAt) {
      setError("Elegí fecha y hora antes de buscar una mesa.");
      return;
    }
    try {
      setChecking(true);
      setError("");
      const data = await tableReservationsApi.availability(projectId, {
        startAt: new Date(form.startAt).toISOString(),
        durationMinutes: Number(form.durationMinutes),
        partySize: Number(form.partySize),
        excludeReservationId: editingId || undefined,
      });
      setAvailability(data);
      if (data?.calendarBlocked) setError("Ese horario está bloqueado en la agenda general.");
    } catch (err) {
      setError(err.message || "No se pudo consultar la disponibilidad.");
    } finally {
      setChecking(false);
    }
  };

  const resetForm = React.useCallback(() => {
    setForm(emptyForm());
    setEditingId("");
    setAvailability(null);
    setError("");
  }, []);

  const openCreate = () => {
    if (!canManage) return;
    resetForm();
    setMessage("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    resetForm();
  };

  const edit = (reservation) => {
    if (!canManage) return;
    const ids = Array.isArray(reservation.tableIds) && reservation.tableIds.length
      ? reservation.tableIds
      : reservation.tableId
        ? [reservation.tableId]
        : [];
    setEditingId(reservation.id);
    setForm({
      fullName: reservation.customer?.fullName || "",
      phone: reservation.customer?.phone || "",
      email: reservation.customer?.email || "",
      startAt: toLocalInput(reservation.startAt),
      partySize: Number(reservation.partySize || 1),
      durationMinutes: Number(reservation.durationMinutes || 90),
      status: reservation.status || "confirmed",
      tableIds: ids,
      notes: reservation.notes || "",
    });
    setAvailability(null);
    setError("");
    setMessage("");
    setFormOpen(true);
  };

  const applyAlternative = (alternative) => {
    setForm((current) => ({ ...current, tableIds: alternative.tableIds || [] }));
    setError("");
    setMessage(`Asignación sugerida aplicada: ${assignmentLabel(alternative.tables)}.`);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    if (!form.fullName.trim() || !form.phone.trim() || !form.startAt) {
      setError("Completá cliente, teléfono, fecha y hora.");
      return;
    }
    if (availability && form.tableIds.length && selectedCapacity < Number(form.partySize || 0)) {
      setError(`Las mesas seleccionadas suman ${selectedCapacity} lugares y la reserva es para ${Number(form.partySize || 0)} personas.`);
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        customer: {
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
        },
        startAt: new Date(form.startAt).toISOString(),
        partySize: Number(form.partySize),
        durationMinutes: Number(form.durationMinutes),
        status: form.status,
        tableIds: form.tableIds,
        notes: form.notes.trim(),
        timezone,
      };
      const successMessage = editingId ? "Reserva gastronómica actualizada." : "Reserva gastronómica creada.";
      if (editingId) await tableReservationsApi.update(projectId, editingId, payload);
      else await tableReservationsApi.create(projectId, payload);
      setFormOpen(false);
      setForm(emptyForm());
      setEditingId("");
      setAvailability(null);
      await load();
      setMessage(successMessage);
    } catch (err) {
      setError(err.message || "No se pudo guardar la reserva.");
    } finally {
      setSaving(false);
    }
  };

  const toggleNotes = (id) => {
    setExpandedNotes((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const availabilityTables = availability?.tables || [];
  const alternatives = availability?.alternatives || [];
  const occupancy = availability?.occupancy;
  const selectableTables = availabilityTables.filter((table) => table.available);
  const selectedCapacity = availabilityTables
    .filter((table) => form.tableIds.includes(table.id))
    .reduce((sum, table) => sum + Number(table.capacity || 0), 0);

  return (
    <Stack spacing={{ xs: 1.75, md: 2.5 }} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 950 }}>Reservas de mesa</Typography>
          <Typography variant="body2" color="text.secondary">Horarios, personas y mesa asignada sin mezclar turnos de servicios.</Typography>
        </Box>
        {canManage ? (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ minHeight: 46, alignSelf: { xs: "stretch", sm: "auto" }, textTransform: "none", fontWeight: 900 }}
          >
            Nueva reserva
          </Button>
        ) : null}
      </Stack>

      {message ? <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert> : null}
      {error && !formOpen ? <Alert severity="error">{error}</Alert> : null}
      {!canManage ? <Alert severity="info">Tu rol permite consultar las reservas, pero no crearlas ni editarlas.</Alert> : null}

      {loading ? <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress /></Box> : null}
      {!loading && reservations.length === 0 ? <Alert severity="info" icon={<EventSeatRoundedIcon />}>Todavía no hay reservas de mesa.</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.2 }}>
        {reservations.map((reservation) => {
          const tables = reservationTables(reservation);
          const capacity = Number(reservation.assignedCapacity || tables.reduce((sum, table) => sum + Number(table.capacity || 0), 0));
          const notesOpen = expandedNotes.has(reservation.id);
          return (
            <Card key={reservation.id} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.75, minWidth: 0 }}>
              <Stack spacing={1.05}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, overflowWrap: "anywhere" }}>{reservation.customer?.fullName || "Cliente"}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: .35, display: "flex", alignItems: "center", gap: .5 }}><AccessTimeRoundedIcon sx={{ fontSize: 16 }} />{dateTimeLabel(reservation.startAt, reservation.timezone)}</Typography>
                  </Box>
                  <Chip size="small" color={STATUS_COLORS[reservation.status] || "default"} label={statusLabel(reservation.status)} sx={{ fontWeight: 850, flex: "0 0 auto" }} />
                </Stack>

                <Stack direction="row" spacing={.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<PeopleAltRoundedIcon />} label={`${reservation.partySize} personas · ${reservation.durationMinutes} min`} />
                  <Chip size="small" variant={tables.length ? "filled" : "outlined"} label={tables.length ? `${assignmentLabel(tables)} · ${capacity} lugares` : "Sin mesa"} sx={{ maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                </Stack>
                {tables.length ? <Typography variant="caption" color="text.secondary">{sectorsLabel(tables)}</Typography> : null}

                {reservation.notes ? (
                  <>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => toggleNotes(reservation.id)}
                      endIcon={<ExpandMoreRoundedIcon sx={{ transform: notesOpen ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
                      sx={{ alignSelf: "flex-start", minHeight: 38, px: .5, textTransform: "none", fontWeight: 850 }}
                    >
                      {notesOpen ? "Ocultar notas" : "Ver notas"}
                    </Button>
                    <Collapse in={notesOpen} unmountOnExit>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)", whiteSpace: "pre-wrap" }}>{reservation.notes}</Typography>
                    </Collapse>
                  </>
                ) : null}

                {canManage ? (
                  <Button variant="outlined" onClick={() => edit(reservation)} sx={{ minHeight: 44, textTransform: "none", fontWeight: 900, alignSelf: { sm: "flex-end" } }}>Editar reserva</Button>
                ) : null}
              </Stack>
            </Card>
          );
        })}
      </Box>

      <Dialog open={canManage && formOpen} onClose={closeForm} fullWidth maxWidth="md" fullScreen={mobile}>
        <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", minHeight: mobile ? "100%" : "auto" }}>
          <DialogTitle sx={{ fontWeight: 950 }}>{editingId ? "Editar reserva" : "Nueva reserva"}</DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 }, flex: 1 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" }, gap: 1.25 }}>
                <TextField label="Cliente" value={form.fullName} onChange={setField("fullName")} required autoFocus={!editingId} />
                <TextField label="Teléfono" value={form.phone} onChange={setField("phone")} required />
                <TextField label="Email" value={form.email} onChange={setField("email")} />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1.5fr .7fr .8fr .9fr" }, gap: 1.25 }}>
                <TextField label="Fecha y hora" type="datetime-local" value={form.startAt} onChange={setField("startAt")} InputLabelProps={{ shrink: true }} required sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }} />
                <TextField label="Personas" type="number" inputProps={{ min: 1, max: 1000 }} value={form.partySize} onChange={setField("partySize")} required />
                <TextField label="Duración" type="number" inputProps={{ min: 15, max: 1440, step: 15 }} value={form.durationMinutes} onChange={setField("durationMinutes")} required />
                <TextField select label="Estado" value={form.status} onChange={setField("status")}>{STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                <Button variant="outlined" onClick={checkAvailability} disabled={checking || saving} sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}>
                  {checking ? "Buscando…" : "Revisar disponibilidad"}
                </Button>
                {form.tableIds.length ? <Button variant="text" onClick={() => setForm((current) => ({ ...current, tableIds: [] }))} disabled={saving} sx={{ minHeight: 44, textTransform: "none" }}>Dejar sin mesa</Button> : null}
                {availability ? <Typography variant="body2" color="text.secondary">{selectableTables.length}/{availabilityTables.length} mesas libres.</Typography> : null}
              </Stack>

              {occupancy ? (
                <Alert severity={occupancy.expectedGuests > occupancy.totalCapacity ? "warning" : "info"}>
                  <strong>{occupancy.expectedGuests} personas previstas.</strong> {occupancy.occupiedTables}/{occupancy.totalTables} mesas comprometidas · {occupancy.unassignedReservations} sin mesa.
                </Alert>
              ) : null}

              {availability ? (
                <TextField
                  select
                  label="Mesas asignadas (opcional)"
                  value={form.tableIds}
                  onChange={setSelectedTables}
                  SelectProps={{ multiple: true, renderValue: (selected) => assignmentLabel(availabilityTables.filter((table) => selected.includes(table.id))) }}
                  helperText={form.tableIds.length ? `Capacidad ${selectedCapacity} para ${form.partySize} personas.` : "Podés guardar la reserva sin mesa."}
                >
                  {availabilityTables.map((table) => <MenuItem key={table.id} value={table.id} disabled={!table.available}>{tableLabel(table)} · {table.capacity} lugares{table.available ? "" : " · ocupada"}</MenuItem>)}
                </TextField>
              ) : null}

              {availability && alternatives.length ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Sugerencias</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))" }, gap: 1 }}>
                    {alternatives.slice(0, 4).map((alternative, index) => (
                      <Card key={alternative.tableIds.join(":")} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>{index === 0 ? "Mejor opción · " : ""}{assignmentLabel(alternative.tables)}</Typography>
                        <Typography variant="caption" color="text.secondary">{alternative.capacity} lugares · {sectorsLabel(alternative.tables)}</Typography>
                        <Button size="small" variant={index === 0 ? "contained" : "outlined"} onClick={() => applyAlternative(alternative)} sx={{ mt: .8, minHeight: 38, textTransform: "none", fontWeight: 850 }}>Usar</Button>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {availability && !availability.calendarBlocked && !alternatives.length ? <Alert severity="warning">No hay capacidad suficiente libre. Podés guardar sin mesa y resolver la asignación después.</Alert> : null}

              {availability?.conflicts?.some((item) => !item.unassigned) ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Conflictos</Typography>
                  <Stack spacing={.8}>{availability.conflicts.filter((item) => !item.unassigned).map((conflict) => <Alert key={conflict.reservationId} severity="warning">{assignmentLabel(conflict.tables)} · {conflict.customerName || conflict.appointmentNumber || "Reserva existente"} · {dateTimeLabel(conflict.startAt, timezone)} a {dateTimeLabel(conflict.endAt, timezone)}.</Alert>)}</Stack>
                </Box>
              ) : null}

              <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={setField("notes")} />
              {error ? <Alert severity="error">{error}</Alert> : null}
              {message && formOpen ? <Alert severity="success">{message}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.25, sm: 2 }, gap: 1, position: { xs: "sticky", sm: "static" }, bottom: 0, bgcolor: "background.paper", borderTop: { xs: "1px solid rgba(25,36,47,.08)", sm: 0 } }}>
            <Button onClick={closeForm} disabled={saving} sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ minHeight: 44, textTransform: "none", fontWeight: 900, flex: { xs: 1, sm: "0 0 auto" } }}>{saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear reserva"}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
}
